import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
} from "@mui/material";
import { WarningAmberRounded } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { forceDeleteAdmissionTerm, getTermDeletePreview } from "./accountsApi";

const fmtMoney = (value) => `$${Number(value || 0).toFixed(2)}`;
const getApiErrorMessage = (error) =>
  error?.response?.data?.message || error?.response?.data?.errorMessage || error?.message || "";

/**
 * Super-admin "force delete" confirmation for a single admission term. Fetches a server preview of
 * everything that will be removed (payments, provider term, verifications, tasks/notes) or shifted,
 * makes the user explicitly acknowledge, then calls the destructive endpoint. Used from both Manage
 * Admissions and the Direct Account term list.
 */
export default function ForceDeleteTermDialog({ open, onClose, cadId, termNumber, onDeleted }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    let active = true;
    if (open && cadId != null && termNumber != null) {
      setPreview(null);
      setAcknowledged(false);
      setLoading(true);
      getTermDeletePreview(cadId, termNumber)
        .then((data) => {
          if (active) setPreview(data);
        })
        .catch((e) => {
          if (active) toast.error(getApiErrorMessage(e) || "Failed to load delete preview");
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }
    return () => {
      active = false;
    };
  }, [open, cadId, termNumber]);

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await forceDeleteAdmissionTerm(cadId, termNumber);
      toast.success(`Term T${termNumber} deleted`);
      onDeleted?.();
      onClose();
    } catch (e) {
      toast.error(getApiErrorMessage(e) || "Failed to delete term");
    } finally {
      setSubmitting(false);
    }
  };

  const canDelete = preview?.canDelete === true;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle className="flex items-center gap-2">
        <WarningAmberRounded className="text-red-500" /> Force delete Term T{termNumber}?
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <div className="flex justify-center py-6">
            <CircularProgress size={24} />
          </div>
        ) : !preview ? (
          <p className="text-sm text-app-text-muted">Could not load the delete preview.</p>
        ) : (
          <>
            <p className="text-sm text-app-text mb-2">
              This <strong>permanently deletes</strong> this term and everything tied to it, then
              shifts later terms up one position. This cannot be undone.
            </p>

            <div className="rounded border bg-app-surface-muted p-3 text-xs flex flex-col gap-1">
              {preview.paymentsCount + preview.refundsCount > 0 && (
                <Row
                  label="Payments / refunds removed"
                  value={`${preview.paymentsCount} payment(s) (${fmtMoney(
                    preview.paymentsTotalAmount
                  )}), ${preview.refundsCount} refund(s)`}
                />
              )}
              {preview.verifiedPaymentsCount > 0 && (
                <Row
                  label="Verified payments"
                  value={`${preview.verifiedPaymentsCount} (${fmtMoney(
                    preview.paymentsTotalAmount
                  )})`}
                  danger
                />
              )}
              {preview.sentToProvider && (
                <Row label="Sent to provider" value="Provider term removed" />
              )}
              {preview.hasPaidProviderInvoice && (
                <Row
                  label="Paid provider invoice"
                  value="Kept — items detached from this term"
                  danger
                />
              )}
              {(preview.commissionClaimed || preview.bonusClaimed) && (
                <Row
                  label="Claimed"
                  value={[
                    preview.commissionClaimed ? "commission" : null,
                    preview.bonusClaimed ? "bonus" : null,
                  ]
                    .filter(Boolean)
                    .join(" + ")}
                />
              )}
              {preview.verificationsCount > 0 && (
                <Row label="Account verifications" value={String(preview.verificationsCount)} />
              )}
              {preview.tasksCount > 0 && (
                <Row label="Term tasks" value={String(preview.tasksCount)} />
              )}
              {preview.notesCount > 0 && (
                <Row label="Account notes" value={String(preview.notesCount)} />
              )}
              {preview.invoiceItemsDetachedCount > 0 && (
                <Row
                  label="Invoice line items"
                  value={`${preview.invoiceItemsDetachedCount} detached (invoice kept)`}
                />
              )}
              <Divider sx={{ my: 0.5 }} />
              <Row
                label="Later terms shifted up"
                value={
                  preview.laterTermsShiftedCount > 0
                    ? `${preview.laterTermsShiftedCount} (T${termNumber + 1}→T${termNumber}, …)`
                    : "none (last term)"
                }
              />
            </div>

            {!canDelete && preview.blockedReason && (
              <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                {preview.blockedReason}
              </div>
            )}

            {canDelete && (
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <span className="text-xs">
                    I understand this permanently deletes the term and its payments.
                  </span>
                }
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions className="py-2 px-3 gap-2">
        <Button onClick={onClose} disabled={submitting} variant="outlined" size="small">
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          disabled={submitting || loading || !canDelete || !acknowledged}
          variant="contained"
          color="error"
          size="small"
          startIcon={submitting ? <CircularProgress size={14} /> : null}
        >
          {submitting ? "Deleting..." : "Delete term"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Row({ label, value, danger }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-app-text-secondary">{label}</span>
      <span className={danger ? "text-red-600 font-medium" : "font-medium"}>{value}</span>
    </div>
  );
}
